import {
  Button,
  Col,
  Form,
  Radio,
  Row,
  Space,
  Typography,
  message,
  Table,
  Modal,
  InputNumber,
  Spin,
  Input,
} from "antd";
import { useEffect, useState } from "react";
import { getProcurementDetails, updateProcurementStageItem, getSwitchMappingsByClusterName, getSwitchMappingsBySiteCode } from "apis";
import JiraTracker from "../../../components/common/JiraTracker";
import SharePointEditor from "../../../components/common/SharepointEditor";
import useProcurementStore from "store/procurement_store";
import {
  ENTITY_TYPES,
  PROCUREMENT_STAGES,
  PROCUREMENT_STATUS,
  PROCUREMENT_TYPES,
  buildStageSK,
} from "types";
import { DatabaseManager } from "helpers/db";
import { getUserIdentity } from "helpers/auth";
import moment from "moment";
import ProcurementStageDetailsFooter from "components/common/ProcurementStageDetailsFooter";

const style = {
  display: "flex",
  flexDirection: "column",
  gap: 15,
  fontWeight: 450,
};

const { Text } = Typography;
const { TextArea } = Input;

const NetworkActionSection = ({ PK, isUserAllowed }) => {
  const [value, setValue] = useState(1);
  const [networkStageData, setNetworkStageData] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [sharePointUrl, setSharePointUrl] = useState("");
  const [jiraIssueKey, setJiraIssueKey] = useState(null);
  const [clusterPortsInfo, setClusterPortsInfo] = useState({});
  const [showResourceEstimation, setShowResourceEstimation] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [sfpType, setSfpType] = useState("Single"); // Add SFP type state with default value

  // Editable NIC counts
  const [editableGbps1PortNics, setEditableGbps1PortNics] = useState(0);
  const [editableSpeedGbpsPortNics, setEditableSpeedGbpsPortNics] = useState(0);
  const [siteCodeSwitchMappings, setSiteCodeSwitchMappings] = useState([]);
  const [siteCodeLoading, setSiteCodeLoading] = useState(false);

  // Add new state for override functionality
  const [autoDetectedValue, setAutoDetectedValue] = useState(null);
  const [overrideComments, setOverrideComments] = useState("");
  const [isOverridden, setIsOverridden] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [tempOverrideValue, setTempOverrideValue] = useState(null);

  const DBManager = new DatabaseManager();

  const {
    procurement_header_data,
    demandIntakeData,
  } = useProcurementStore((state) => ({
    procurement_header_data: state.procurement_header_data,
    demandIntakeData: state.demandIntakeData,
  }));

  // Get procurement type from store instead of API call
  const procurementType = procurement_header_data?.procurement_type;
  const siteCode = procurement_header_data?.site_code?.toLowerCase() || "wpp";
  console.log("Procurement type from store:", procurementType);
  console.log("Site code from store:", siteCode);

  // Extract node count from estimated_tshirt_size and calculate required ports
  const extractNodeCount = (tshirtSize) => {
    if (!tshirtSize) return 0;
    const match = tshirtSize.match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  };

  const nodeCount = extractNodeCount(demandIntakeData?.estimated_tshirt_size);
  const requiredPorts = nodeCount * 2;

  console.log("T-shirt size:", demandIntakeData?.estimated_tshirt_size);
  console.log("Extracted node count:", nodeCount);
  console.log("Required ports:", requiredPorts);

  // Utility function for API calls with retry logic
  const apiCallWithRetry = async (
    apiFunction,
    maxRetries = 3,
    delay = 1000
  ) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await Promise.race([
          apiFunction(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Request timeout")), 30000)
          ),
        ]);
        return result;
      } catch (error) {
        console.warn(`API call attempt ${attempt} failed:`, error);

        if (attempt === maxRetries) {
          throw error;
        }

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delay * attempt));
      }
    }
  };

  // Initialize editable values when demandIntakeData changes
  useEffect(() => {
    if (demandIntakeData) {
      setEditableGbps1PortNics(demandIntakeData.gbps_4_port_NICs || 0);
      setEditableSpeedGbpsPortNics(
        demandIntakeData.speed_gbps_4_port_NICs || 0
      );
    }
  }, [demandIntakeData]);

  useEffect(() => {
    const fetchData = async () => {
      if (!PK) {
        message.error("PK is required to load procurement data.");
        setDataLoading(false);
        return;
      }

      try {
        setDataLoading(true);

        const response = await apiCallWithRetry(() =>
          getProcurementDetails(PK, "Network")
        );

        if (response?.success && response.data?.length) {
          const stageItem = response.data[0];
          setNetworkStageData(stageItem);

          if (stageItem?.assessment_folder) {
            setSharePointUrl(stageItem.assessment_folder);
          }
          if (stageItem?.jira_key) {
            setJiraIssueKey(stageItem.jira_key);
          }

          // Load saved network assessment data if exists
          if (stageItem?.network_assessment) {
            const assessment = stageItem.network_assessment;
            setShowResourceEstimation(true);

            if (assessment.resource_requirement === "no_new_resources") {
              setValue(1);
            } else if (
              assessment.resource_requirement === "new_switches_required"
            ) {
              setValue(2);
            }

            // Load override data if exists
            if (assessment.override_data) {
              const overrideData = assessment.override_data;
              setAutoDetectedValue(overrideData.auto_detected_value);
              setOverrideComments(overrideData.comments || "");
              setIsOverridden(overrideData.is_overridden || false);
            }

            // Load saved SFP type if available, default to "Single"
            setSfpType(assessment.sfp_type || "Single");

            // Load saved NIC counts if available
            if (assessment?.sfp_breakdown) {
              setEditableGbps1PortNics(
                assessment.sfp_breakdown.gbps_1_nics?.nic_count || 0
              );
              setEditableSpeedGbpsPortNics(
                assessment.sfp_breakdown.dynamic_speed_nics?.nic_count || 0
              );
            }
          }

          // Set edit mode based on assessment status
          const assessmentStatus = stageItem?.assessment_status;
          const stageStatus = stageItem?.stage_status;

          if (
            assessmentStatus === "Assessment Complete" &&
            stageStatus !== "Approved"
          ) {
            setIsEditMode(false);
          } else if (
            assessmentStatus === "Reviewed" &&
            stageStatus === "Approved"
          ) {
            setIsEditMode(false);
          } else {
            setIsEditMode(true);
          }
        }
      } catch (error) {
        console.error("Error fetching procurement data:", error);
        message.error(`Failed to load procurement data: ${error.message}`);
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
  }, [PK]);

  const procurement_type = procurement_header_data?.procurement_type;
  const cluster_name = demandIntakeData?.cluster_name || "";

  // Network Resource Estimation Logic with editable values - Move before useEffects
  const nicSpeed = demandIntakeData?.nic_speed || "N/A";

  const sfpsRequired1Gbps = (editableGbps1PortNics || 0) * 4;
  const sfpsRequiredSpeed = (editableSpeedGbpsPortNics || 0) * 4;
  const totalSFPsRequired = sfpsRequired1Gbps + sfpsRequiredSpeed;

  function parseNumberString(s) {
    if (s == null) return 0;
    const cleaned = String(s).trim().replace(/,/g, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }

  const totalOpenPorts =
    parseNumberString(clusterPortsInfo?.Open_ports_switch1) +
    parseNumberString(clusterPortsInfo?.Open_ports_switch2);

  const handleEstimateNetworkResources = () => {
    setShowResourceEstimation(true);
  };

  useEffect(() => {
    const fetchClusterInfo = async () => {
      if (!cluster_name) {
        console.warn("Cluster name is not available");
        return;
      }

      try {
        const response = await apiCallWithRetry(() =>
          getSwitchMappingsByClusterName(cluster_name)
        );

        if (response?.success && response.data?.length) {
          setClusterPortsInfo(response.data[0] || {});
        } else {
          console.warn("No switch mappings found for cluster:", cluster_name);
          setClusterPortsInfo({});
        }
      } catch (error) {
        console.error("Error fetching cluster information:", error);
        message.error(`Error fetching cluster information: ${error.message}`);
        setClusterPortsInfo({});
      }
    };

    if (procurement_type === PROCUREMENT_TYPES.EXPANSION && cluster_name) {
      fetchClusterInfo();
    }
  }, [procurement_type, cluster_name]);

  // Add useEffect for EXPANSION auto-detection
  useEffect(() => {
    if (procurementType === 'EXPANSION' && showResourceEstimation && totalOpenPorts !== undefined && totalSFPsRequired !== undefined) {
      const autoValue = totalSFPsRequired <= totalOpenPorts ? 1 : 2;
      
      // Only set auto-detected value if it hasn't been set yet or if we're not in an override state
      if (autoDetectedValue === null || !isOverridden) {
        setAutoDetectedValue(autoValue);
        if (!isOverridden) {
          setValue(autoValue);
        }
      }
      
      console.log("Auto-detected value for EXPANSION procurement:", autoValue);
    }
  }, [procurementType, showResourceEstimation, totalOpenPorts, totalSFPsRequired, autoDetectedValue, isOverridden]);

  const handleRadioOnChange = (e) => {
    const newValue = e.target.value;
    
    // Check if user is overriding the auto-detected value
    if (autoDetectedValue !== null && newValue !== autoDetectedValue && !isOverridden) {
      setTempOverrideValue(newValue);
      setShowOverrideModal(true);
      return;
    }
    
    setValue(newValue);
  };

  const handleSfpTypeChange = (e) => {
    setSfpType(e.target.value);
  };

  const handleOverrideConfirm = () => {
    setValue(tempOverrideValue);
    setIsOverridden(true);
    setShowOverrideModal(false);
  };

  const handleOverrideCancel = () => {
    setTempOverrideValue(null);
    setShowOverrideModal(false);
  };

  useEffect(() => {
    const fetchSiteCodeSwitchMappings = async () => {
      if (!siteCode) {
        console.warn("Site code is not available");
        return;
      }

      try {
        setSiteCodeLoading(true);
        console.log("Fetching switch mappings for site code:", siteCode);
        const response = await apiCallWithRetry(() => getSwitchMappingsBySiteCode(siteCode));

        if (response?.success && response.data?.length) {
          setSiteCodeSwitchMappings(response.data);
          
          // Auto-set toggle based on switch pair capacity
          const hasSufficientSwitchPair = response.data.some(item => {
            const totalOpenPorts = (item.Open_ports_switch1 || 0) + (item.Open_ports_switch2 || 0);
            return totalOpenPorts >= requiredPorts;
          });
          
          const autoValue = hasSufficientSwitchPair ? 1 : 2;
          setAutoDetectedValue(autoValue);
          setValue(autoValue);
          
          console.log("Auto-detected value for NEW procurement:", autoValue);
        } else {
          console.warn("No switch mappings found for site code:", siteCode);
          setSiteCodeSwitchMappings([]);
          const autoValue = 2;
          setAutoDetectedValue(autoValue);
          setValue(autoValue);
        }
      } catch (error) {
        console.error("Error fetching site code switch mappings:", error);
        message.error(`Error fetching site code switch mappings: ${error.message}`);
        setSiteCodeSwitchMappings([]);
        const autoValue = 2;
        setAutoDetectedValue(autoValue);
        setValue(autoValue);
      } finally {
        setSiteCodeLoading(false);
      }
    };

    // Only fetch site code mappings for NEW procurement type
    console.log("Current procurement type for site code check:", procurementType);
    if (procurementType === 'NEW' && siteCode) {
      console.log("Fetching site code switch mappings for NEW procurement type");
      fetchSiteCodeSwitchMappings();
    } else {
      console.log("Skipping site code switch mappings - not NEW procurement type or no site code");
    }
  }, [procurementType, siteCode, requiredPorts]);

  // Remove duplicate Network Resource Estimation Logic section that was below
  const handleReassessment = () => {
    // Compare total SFPs required with total open ports
    if (totalSFPsRequired <= totalOpenPorts) {
      setValue(1);
      Modal.success({
        title: "Network Resource Assessment",
        content: (
          <div>
            <p>
              <strong>Total SFPs Required:</strong> {totalSFPsRequired}
            </p>
            <p>
              <strong>Total Open Ports Available:</strong> {totalOpenPorts}
            </p>
            <p>
              <strong>Result:</strong> No new network resources required.
              Existing switches can handle the request.
            </p>
          </div>
        ),
      });
    } else {
      setValue(2);
      Modal.warning({
        title: "Network Resource Assessment",
        content: (
          <div>
            <p>
              <strong>Total SFPs Required:</strong> {totalSFPsRequired}
            </p>
            <p>
              <strong>Total Open Ports Available:</strong> {totalOpenPorts}
            </p>
            <p>
              <strong>Shortage:</strong> {totalSFPsRequired - totalOpenPorts}{" "}
              ports
            </p>
            <p>
              <strong>Result:</strong> New network resources required. Existing
              switches cannot handle the request.
            </p>
          </div>
        ),
      });
    }
  };

  const handleEditMode = () => {
    setIsEditMode(true);
  };

  const buildBaseStage = () => ({
    PK: { S: PK },
    SK: { S: buildStageSK(PROCUREMENT_STAGES.Network) },
    stage_name: { S: "Network" },
    entity_type: { S: "ProcurementStage" },
  });

  const buildNetworkAssessmentData = () => {
    const portShortage = Math.max(0, totalSFPsRequired - totalOpenPorts);

    return {
      resource_requirement:
        value === 1 ? "no_new_resources" : "new_switches_required",
      sfp_type: sfpType,
      total_open_ports: totalOpenPorts,
      total_sfps_required: totalSFPsRequired,
      override_data: {
        auto_detected_value: autoDetectedValue,
        user_selected_value: value,
        is_overridden: isOverridden,
        comments: overrideComments || ""
      },
      sfp_breakdown: {
        gbps_1_nics: {
          nic_count: editableGbps1PortNics || 0,
          sfps_required: sfpsRequired1Gbps,
        },
        dynamic_speed_nics: {
          nic_speed: nicSpeed,
          nic_count: editableSpeedGbpsPortNics || 0,
          sfps_required: sfpsRequiredSpeed,
        },
      },
      assessment_result:
        totalSFPsRequired <= totalOpenPorts
          ? "sufficient_capacity"
          : "insufficient_capacity",
      port_shortage: portShortage,
      cluster_info: {
        cluster_name: cluster_name || "N/A",
        switch1_name: clusterPortsInfo?.SWITCH1_NAME || "N/A",
        switch2_name: clusterPortsInfo?.SWITCH2_NAME || "N/A",
        switch1_open_ports: clusterPortsInfo?.Open_ports_switch1 || 0,
        switch2_open_ports: clusterPortsInfo?.Open_ports_switch2 || 0,
      },
    };
  };

  const handleAssessmentComplete = async () => {
    if (!PK) {
      message.error("PK is required to complete assessment.");
      return;
    }

    try {
      const networkAssessmentData = buildNetworkAssessmentData();

      const updatedStage = {
        ...(networkStageData || buildBaseStage()),
        assessment_status: "Assessment Complete",
        stage_status: "Submitted",
        network_assessment: networkAssessmentData,
        assessment_timestamp: new Date().toISOString(),
      };

      const response = await apiCallWithRetry(() =>
        updateProcurementStageItem(updatedStage)
      );

      if (response?.success) {
        message.success("Network assessment completed and saved successfully.");
        setNetworkStageData(updatedStage);
        setIsEditMode(false);
      } else {
        message.error("Failed to complete assessment.");
      }
    } catch (error) {
      console.error("Error completing assessment:", error);
      message.error(`Error completing assessment: ${error.message}`);
    }
  };

  const handleValidate = async () => {
    if (!PK) {
      message.error("PK is required to review assessment.");
      return;
    }

    try {
      const networkAssessmentData = buildNetworkAssessmentData();

      const updatedStage = {
        ...(networkStageData || buildBaseStage()),
        assessment_status: "Reviewed",
        stage_status: "Approved",
        network_assessment: networkAssessmentData,
        validation_timestamp: new Date().toISOString(),
      };

      const response = await apiCallWithRetry(() =>
        updateProcurementStageItem(updatedStage)
      );

      if (response?.success) {
        message.success("Network assessment reviewed and saved successfully.");
        setNetworkStageData(updatedStage);
        setIsEditMode(false);
        const primaryKey = PK;
        const user_email = (await getUserIdentity()).email;
        const created_at_timestamp = moment().toISOString();
        await DBManager.createObject(
          process.env.REACT_APP_DYNAMO_DB_PROCUREMENTS || "",
          {
            PK: primaryKey,
            SK: buildStageSK(PROCUREMENT_STAGES.Site),
            entity_type: ENTITY_TYPES.ProcurementStage,
            stage_name: PROCUREMENT_STAGES.Site,
            stage_status: PROCUREMENT_STATUS.Initial,
            created_at: created_at_timestamp,
            created_by: user_email,
          }
        );
      } else {
        message.error("Failed to review assessment.");
      }
    } catch (error) {
      console.error("Error reviewing assessment:", error);
      message.error(`Error reviewing assessment: ${error.message}`);
    }
  };

  const closedPortsSwitch1 = Math.max(
    0,
    (clusterPortsInfo?.Total_ports_switch1 || 0) -
    (clusterPortsInfo?.Open_ports_switch1 || 0)
  );
  const closedPortsSwitch2 = Math.max(
    0,
    (clusterPortsInfo?.Total_ports_switch2 || 0) -
    (clusterPortsInfo?.Open_ports_switch2 || 0)
  );

  const tableData = [
    {
      key: "1",
      switchName: clusterPortsInfo?.SWITCH1_NAME || "N/A",
      totalPorts: clusterPortsInfo?.Total_ports_switch1 || 0,
      openPorts: clusterPortsInfo?.Open_ports_switch1 || 0,
      closedPorts: closedPortsSwitch1,
    },
    {
      key: "2",
      switchName: clusterPortsInfo?.SWITCH2_NAME || "N/A",
      totalPorts: clusterPortsInfo?.Total_ports_switch2 || 0,
      openPorts: clusterPortsInfo?.Open_ports_switch2 || 0,
      closedPorts: closedPortsSwitch2,
    },
    {
      key: "3",
      switchName: "Total",
      totalPorts:
        parseNumberString(clusterPortsInfo?.Total_ports_switch2) +
        parseNumberString(clusterPortsInfo?.Total_ports_switch1),
      openPorts: totalOpenPorts,
      closedPorts: closedPortsSwitch1 + closedPortsSwitch2,
    },
  ];

  const ClusterInfoColumns = [
    {
      title: "Switch Name",
      dataIndex: "switchName",
      key: "switchName",
    },
    {
      title: "Total Ports",
      dataIndex: "totalPorts",
      key: "totalPorts",
    },
    {
      title: "Open Ports",
      dataIndex: "openPorts",
      key: "openPorts",
    },
    {
      title: "Closed Ports",
      dataIndex: "closedPorts",
      key: "closedPorts",
    },
  ];

  const resourceEstimationData = [
    {
      key: "1",
      nicType: "1 Gbps Port NICs",
      nicCount: editableGbps1PortNics || 0,
      sfpsRequired: sfpsRequired1Gbps,
    },
    {
      key: "2",
      nicType: `${nicSpeed} Gbps Port NICs`,
      nicCount: editableSpeedGbpsPortNics || 0,
      sfpsRequired: sfpsRequiredSpeed,
    },
    {
      key: "3",
      nicType: "Total SFPs Required",
      nicCount: "-",
      sfpsRequired: totalSFPsRequired,
    },
  ];

  // Determine if inputs should be disabled
  const isInputDisabled = !isUserAllowed || !isEditMode;
  const isAssessmentComplete =
    networkStageData?.assessment_status === "Assessment Complete";
  const isReviewed = networkStageData?.assessment_status === "Reviewed";
  const isApproved = networkStageData?.stage_status === "Approved";

  const resourceEstimationColumns = [
    {
      title: "NIC Type",
      dataIndex: "nicType",
      key: "nicType",
    },
    {
      title: "NIC Count",
      dataIndex: "nicCount",
      key: "nicCount",
      render: (text, record) => {
        if (record.key === "1") {
          return (
            <InputNumber
              min={0}
              value={editableGbps1PortNics || 0}
              onChange={(value) => setEditableGbps1PortNics(value || 0)}
              disabled={isInputDisabled}
              style={{
                width: 80,
                backgroundColor: isInputDisabled ? "#f5f5f5" : "white",
              }}
            />
          );
        } else if (record.key === "2") {
          return (
            <InputNumber
              min={0}
              value={editableSpeedGbpsPortNics || 0}
              onChange={(value) => setEditableSpeedGbpsPortNics(value || 0)}
              disabled={isInputDisabled}
              style={{
                width: 80,
                backgroundColor: isInputDisabled ? "#f5f5f5" : "white",
              }}
            />
          );
        }
        return text;
      },
    },
    {
      title: "SFPs Required",
      dataIndex: "sfpsRequired",
      key: "sfpsRequired",
      render: (text, record) => {
        if (record.key === "1") {
          return (editableGbps1PortNics || 0) * 4;
        } else if (record.key === "2") {
          return (editableSpeedGbpsPortNics || 0) * 4;
        } else if (record.key === "3") {
          return (
            (editableGbps1PortNics || 0) * 4 +
            (editableSpeedGbpsPortNics || 0) * 4
          );
        }
        return text;
      },
    },
  ];

  const siteCodeTableData = siteCodeSwitchMappings.map((item, index) => ({
    key: index.toString(),
    clusterName: item.ClusterName || "N/A",
    switch1: item.SWITCH1_NAME || "N/A",
    switch2: item.SWITCH2_NAME || "N/A",
    openPortsSwitch1: item.Open_ports_switch1 || 0,
    openPortsSwitch2: item.Open_ports_switch2 || 0,
    totalOpenPorts: (item.Open_ports_switch1 || 0) + (item.Open_ports_switch2 || 0),
    hasSufficientCapacity: ((item.Open_ports_switch1 || 0) + (item.Open_ports_switch2 || 0)) >= requiredPorts,
  }));

  const siteCodeTableColumns = [
    {
      title: "Cluster Name",
      dataIndex: "clusterName",
      key: "clusterName",
    },
    {
      title: "Switch 1",
      dataIndex: "switch1",
      key: "switch1",
    },
    {
      title: "Switch 2",
      dataIndex: "switch2",
      key: "switch2",
    },
    {
      title: "Switch 1 Open Ports",
      dataIndex: "openPortsSwitch1",
      key: "openPortsSwitch1",
    },
    {
      title: "Switch 2 Open Ports",
      dataIndex: "openPortsSwitch2",
      key: "openPortsSwitch2",
    },
    {
      title: "Total Open Ports",
      dataIndex: "totalOpenPorts",
      key: "totalOpenPorts",
      sorter: (a, b) => a.totalOpenPorts - b.totalOpenPorts,
      defaultSortOrder: 'descend',
    },
  ];

  // Don't render if essential data is missing
  if (!PK) {
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        <Text type="danger">Error: PK is required to load this component.</Text>
      </div>
    );
  }

  // Show loading spinner while data is being fetched
  if (dataLoading) {
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>Loading network data...</Text>
        </div>
      </div>
    );
  }

  return (
    <Form disabled={!isUserAllowed}>
      {/* Uncomment this after Lambda is ready */}
      {/* <Row style={{ marginBottom: 20 }}>
        <Col>
          <Button type="primary" onClick={handleSearchHosts} loading={loading}>
            Search for Hosts
          </Button>
        </Col>
      </Row> */}

      {/* Cluster Info Table - Only for EXPANSION procurement type */}
      {procurementType === 'EXPANSION' && (
        <Row style={{ marginBottom: 30 }}>
          <Col span={24}>
            <Table
              dataSource={tableData}
              columns={ClusterInfoColumns}
              pagination={false}
              bordered
            />
          </Col>
        </Row>
      )}

      {/* Show Estimate button only if assessment is not complete or in edit mode */}
      {(!isAssessmentComplete || isEditMode) && procurementType === 'EXPANSION' && (
        <Row style={{ marginBottom: 20 }}>
          <Col>
            <Button
              type="primary"
              onClick={handleEstimateNetworkResources}
              disabled={isApproved}
              style={{
                backgroundColor: isApproved ? "#f5f5f5" : undefined,
                borderColor: isApproved ? "#d9d9d9" : undefined,
                color: isApproved ? "#00000040" : undefined,
              }}
            >
              Estimate Network Resources
            </Button>
          </Col>
        </Row>
      )}

      {showResourceEstimation && procurementType === 'EXPANSION' && (
        <Row style={{ marginBottom: 30 }}>
          <Col span={24}>
            <Text strong style={{ marginBottom: 10, display: "block" }}>
              Network Resource Estimation {!isEditMode && "(Read Only)"}
            </Text>
            <Table
              dataSource={resourceEstimationData}
              columns={resourceEstimationColumns}
              pagination={false}
              bordered
              style={{
                backgroundColor: !isEditMode ? "#f9f9f9" : "white",
              }}
            />
            {isEditMode && procurementType === 'EXPANSION' && (
              <div style={{ marginTop: 10 }}>
                <Button type="primary" onClick={handleReassessment}>
                  Re-assess Network Requirements
                </Button>
              </div>
            )}
          </Col>
        </Row>
      )}

      {/* Site Code Switch Mappings Table - Only for NEW procurement type */}
      {procurementType === 'NEW' && (
        <Row style={{ marginBottom: 30 }}>
          <Col span={24}>
            <Text strong style={{ marginBottom: 10, display: 'block' }}>
              Switch Mappings for Site Code "{siteCode?.toUpperCase()}" ({nodeCount} nodes require {requiredPorts} ports)
            </Text>
            <Table
              dataSource={siteCodeTableData}
              columns={siteCodeTableColumns}
              pagination={false}
              bordered
              loading={siteCodeLoading}
              defaultSortOrder="descend"
              sortDirections={['descend', 'ascend']}
              rowClassName={(record) => {
                return record.hasSufficientCapacity ? 'sufficient-capacity-row' : '';
              }}
              onChange={(pagination, filters, sorter) => {
                // Default sort by totalOpenPorts descending
              }}
              locale={{
                emptyText: siteCodeLoading ? "Loading..." : `No switch mappings found for site code ${siteCode?.toUpperCase()}`
              }}
            />
            <style jsx>{`
              .sufficient-capacity-row {
                background-color: rgba(208, 242, 178, 1) !important;
              }
              .sufficient-capacity-row:hover {
                background-color: #f6ffed  !important;
              }
            `}</style>
          </Col>
        </Row>
      )}

      {/* Override Information Display */}
      {isOverridden && (
        <Row style={{ marginBottom: 20 }}>
          <Col span={24}>
            <div style={{ 
              background: '#fff7e6', 
              border: '1px solid #ffd591', 
              borderRadius: 4, 
              padding: 12 
            }}>
              <Text strong style={{ color: '#d46b08' }}>Override Detected:</Text>
              <div style={{ marginTop: 8 }}>
                <Text>Auto-detected: {autoDetectedValue === 1 ? 'No new network resources required' : 'New switches required'}</Text>
                <br />
                <Text>User selected: {value === 1 ? 'No new network resources required' : 'New switches required'}</Text>
                {overrideComments && (
                  <>
                    <br />
                    <Text strong>Comments: </Text>
                    <Text>{overrideComments}</Text>
                  </>
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}

      <Row>
        <Col span={24}>
          <Radio.Group
            style={style}
            onChange={handleRadioOnChange}
            value={value}
            disabled={isInputDisabled}
          >
            <Radio value={1} disabled={isInputDisabled}>
              No new network resources required
            </Radio>
            <div style={{ display: "flex", alignItems: "center", gap: "8%" }}>
              <Radio value={2} disabled={isInputDisabled}>
                New switches required
              </Radio>
            </div>
          </Radio.Group>
        </Col>
      </Row>

      {/* SFP Type Radio Button Control */}
      <Row style={{ marginTop: "20px" }}>
        <Col span={24}>
          <Text strong style={{ display: "block", marginBottom: "10px" }}>
            SFP Type:
          </Text>
          <Radio.Group
            style={style}
            onChange={handleSfpTypeChange}
            value={sfpType}
            disabled={isInputDisabled}
          >
            <Radio value="Single" disabled={isInputDisabled}>
              Single
            </Radio>
            <Radio value="Multi" disabled={isInputDisabled}>
              Multi
            </Radio>
          </Radio.Group>
        </Col>
      </Row>

      {value === 2 && (
        <Row style={{ marginTop: "35px" }}>
          <Col span={24}>
            <SharePointEditor
              PK={PK}
              stageData={networkStageData}
              setStageData={setNetworkStageData}
              initialUrl={sharePointUrl}
            />
          </Col>
        </Row>
      )}

      <Row>
        <Col span={24}>
          <Space style={{ marginTop: "35px" }}>
            {/* Assessment Complete / Edit Button Logic */}
            {!isAssessmentComplete ? (
              <Form.Item>
                <Button
                  type="primary"
                  onClick={handleAssessmentComplete}
                  disabled={isApproved}
                  style={{
                    backgroundColor: isApproved ? "#f5f5f5" : undefined,
                    borderColor: isApproved ? "#d9d9d9" : undefined,
                    color: isApproved ? "#00000040" : undefined,
                  }}
                >
                  Assessment Complete
                </Button>
              </Form.Item>
            ) : !isApproved ? (
              <Form.Item>
                <Button type="default" onClick={handleEditMode}>
                  Edit
                </Button>
              </Form.Item>
            ) : (
              <Form.Item>
                <Button
                  type="default"
                  disabled={true}
                  style={{
                    backgroundColor: "#f5f5f5",
                    borderColor: "#d9d9d9",
                    color: "#00000040",
                  }}
                >
                  Edit
                </Button>
              </Form.Item>
            )}

            {/* Save button when in edit mode after assessment complete */}
            {isEditMode && isAssessmentComplete && !isApproved && (
              <Form.Item>
                <Button type="primary" onClick={handleAssessmentComplete}>
                  Save Changes
                </Button>
              </Form.Item>
            )}

            {/* Review Button - only show if assessment is complete and not yet reviewed */}
            {isAssessmentComplete && !isReviewed ? (
              <Form.Item>
                <Button type="primary" onClick={handleValidate}>
                  Review
                </Button>
              </Form.Item>
            ) : (
              isApproved && (
                <Form.Item>
                  <Button
                    type="primary"
                    disabled={true}
                    style={{
                      backgroundColor: "#f5f5f5",
                      borderColor: "#d9d9d9",
                      color: "#00000040",
                    }}
                  >
                    Reviewed
                  </Button>
                </Form.Item>
              )
            )}
          </Space>
          <ProcurementStageDetailsFooter/>
        </Col>
      </Row>

      <Row style={{ marginTop: 20 }}>
        <Col span={24}>
          <JiraTracker
            PK={PK}
            stageData={networkStageData}
            setStageData={setNetworkStageData}
            initialKey={jiraIssueKey}
          />
        </Col>
      </Row>

      {/* Override Comment Modal */}
      <Modal
        title="Override Confirmation"
        open={showOverrideModal}
        onOk={handleOverrideConfirm}
        onCancel={handleOverrideCancel}
        okText="Confirm Override"
        cancelText="Cancel"
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>
            You are overriding the system recommendation. 
          </Text>
          <br />
          <Text strong>
            System recommended: {autoDetectedValue === 1 ? 'No new network resources required' : 'New switches required'}
          </Text>
          <br />
          <Text strong>
            You selected: {tempOverrideValue === 1 ? 'No new network resources required' : 'New switches required'}
          </Text>
        </div>
        <div>
          <Text strong>Please provide comments for this override:</Text>
          <TextArea
            rows={4}
            value={overrideComments}
            onChange={(e) => setOverrideComments(e.target.value)}
            placeholder="Enter your comments explaining why you're overriding the system recommendation..."
            style={{ marginTop: 8 }}
          />
        </div>
      </Modal>
    </Form>
  );
};

export default NetworkActionSection;